import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

import {
  PM_V2_Template_Interface,
  PM_V2_Template_Detail_Interface,
  PM_V2_Template_Save_Interface,
  PM_V2_Template_Item_Interface,
  PM_V2_Assignment_Interface,
  PM_V2_Assignment_Detail_Interface,
  PM_V2_Assignment_Save_Interface,
  PM_V2_Assignment_Batch_Result_Interface,
  PM_V2_Due_Interface,
  PM_V2_Due_Equipment_Interface,
  PM_V2_Due_Equipment_Filter_Interface,
  PM_V2_Due_Equipment_Item_Interface,
  PM_V2_Due_Execution_History_Interface,
  PM_V2_Execution_Interface,
  PM_V2_Execution_Save_Interface,
  PM_V2_Execution_Approve_Interface,
  PM_V2_Execution_Context_Interface,
  PM_V2_Execution_History_Interface,
  PM_V2_Device_Interface
} from '../../Interface/PM_V2_Interface';

@Injectable({
  providedIn: 'root'
})
export class PM_V2_Service {

  private BaseUrl = environment.BaseUrl;

  constructor(private http: HttpClient) { }

  // -------------------------
  // Template
  // -------------------------

  GetTemplate(): Observable<PM_V2_Template_Interface[]> {
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Template')
      .pipe(map(data => (data || []).map(x => this.NormalizeTemplate(x))));
  }

  GetTemplateActiveList(): Observable<PM_V2_Template_Interface[]> {
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Template/GetActiveList')
      .pipe(map(data => (data || []).map(x => this.NormalizeTemplate(x))));
  }

  GetTemplateSingle(id: number): Observable<PM_V2_Template_Detail_Interface> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Template/GetSingle', { params })
      .pipe(map(data => ({
        model: this.NormalizeTemplate(data ? data.model : null),
        items: ((data && data.items) || []).map((x: any) => this.NormalizeTemplateItem(x))
      })));
  }

  AddTemplate(model: PM_V2_Template_Save_Interface): Observable<PM_V2_Template_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Template/Add', model)
      .pipe(map(data => this.NormalizeTemplate(data)));
  }

  EditTemplate(model: PM_V2_Template_Save_Interface): Observable<PM_V2_Template_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Template/Edit', model)
      .pipe(map(data => this.NormalizeTemplate(data)));
  }

  DeleteTemplate(id: number): Observable<PM_V2_Template_Interface> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Template/Delete', { params })
      .pipe(map(data => this.NormalizeTemplate(data)));
  }

  AddTemplateItem(model: PM_V2_Template_Item_Interface): Observable<PM_V2_Template_Item_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Template/AddItem', { model })
      .pipe(map(data => this.NormalizeTemplateItem(data)));
  }

  EditTemplateItem(model: PM_V2_Template_Item_Interface): Observable<PM_V2_Template_Item_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Template/EditItem', { model })
      .pipe(map(data => this.NormalizeTemplateItem(data)));
  }

  DeleteTemplateItem(id: number): Observable<PM_V2_Template_Item_Interface> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Template/DeleteItem', { params })
      .pipe(map(data => this.NormalizeTemplateItem(data)));
  }

  // -------------------------
  // Assignment
  // -------------------------

  GetAssignment(templateTitle: string, groupId: number, subGroupId: number, keyword: string, active: boolean): Observable<PM_V2_Assignment_Interface[]> {
    const params = new HttpParams()
      .set('templateTitle', templateTitle || '')
      .set('groupId', String(groupId || 0))
      .set('subGroupId', String(subGroupId || 0))
      .set('keyword', keyword || '')
      .set('active', String(active));
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Assignment', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeAssignment(x))));
  }

  GetAssignmentSingle(id: number): Observable<PM_V2_Assignment_Detail_Interface> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Assignment/GetSingle', { params })
      .pipe(map(data => ({
        model: this.NormalizeAssignment(data ? data.model : null),
        items: ((data && data.items) || []).map((x: any) => this.NormalizeAssignmentItem(x))
      })));
  }

  GetDevicePM(DeviceBehfa_ID: number): Observable<any[]> {
    const params = new HttpParams()
      .set('DeviceBehfa_ID', String(DeviceBehfa_ID));

    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Assignment/GetDevicePM', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeMixed(x))));
  }

  GetDue(templateTitle: string, groupId: number, subGroupId: number, keyword: string,
    active: boolean, dueWindow: string): Observable<PM_V2_Due_Interface[]> {
    const params = new HttpParams()
      .set('templateTitle', templateTitle || '')
      .set('groupId', String(groupId || 0))
      .set('subGroupId', String(subGroupId || 0))
      .set('keyword', keyword || '')
      .set('active', String(active))
      .set('dueWindow', dueWindow || 'all');
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Assignment/GetDue', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeMixed(x) as PM_V2_Due_Interface)));
  }

  GetDueEquipmentList(filter: PM_V2_Due_Equipment_Filter_Interface): Observable<PM_V2_Due_Equipment_Interface[]> {
    const params = new HttpParams()
      .set('TemplateTitle', filter.TemplateTitle || '')
      .set('Active', String(filter.Active))
      .set('GroupId', String(filter.GroupId || 0))
      .set('SubGroupId', String(filter.SubGroupId || 0))
      .set('Keyword', filter.Keyword || '')
      .set('DueWindow', filter.DueWindow || 'all');
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Due/GetDueEquipmentList', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeMixed(x) as PM_V2_Due_Equipment_Interface)));
  }

  GetDueEquipmentItems(equipmentId: number): Observable<PM_V2_Due_Equipment_Item_Interface[]> {
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Due/GetDueEquipmentItems/' + equipmentId)
      .pipe(map(data => (data || []).map(x => this.NormalizeMixed(x) as PM_V2_Due_Equipment_Item_Interface)));
  }

  GetDueExecutionHistory(equipmentId: number, templateId: number): Observable<PM_V2_Due_Execution_History_Interface[]> {
    const params = new HttpParams()
      .set('equipmentId', String(equipmentId))
      .set('templateId', String(templateId));
    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Due/GetExecutionHistory', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeMixed(x) as PM_V2_Due_Execution_History_Interface)));
  }

  AddAssignment(model: PM_V2_Assignment_Save_Interface): Observable<PM_V2_Assignment_Batch_Result_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Assignment/Add', model)
      .pipe(map(data => ({
        CreatedCount: this.Pick(data, 'CreatedCount', 'createdCount') || 0,
        SkippedDuplicateCount: this.Pick(data, 'SkippedDuplicateCount', 'skippedDuplicateCount') || 0
      })));
  }

  DeactivateAssignments(ids: number[]): Observable<any> {
    return this.http.post<any>(
      this.BaseUrl + 'Daro/PM_V2_Assignment/Deactivate',
      { ids }
    );
  }

  DeleteAssignmentItem(id: number): Observable<any> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http.get<any>(
      this.BaseUrl + 'Daro/PM_V2_Assignment/DeleteItem',
      { params }
    );
  }

  // -------------------------
  // Execution
  // -------------------------

  GetExecutionByDevice(DeviceBehfa_ID: number): Observable<PM_V2_Execution_Interface[]> {
    const params = new HttpParams()
      .set('DeviceBehfa_ID', String(DeviceBehfa_ID));

    return this.http
      .get<any[]>(this.BaseUrl + 'Daro/PM_V2_Execution', { params })
      .pipe(map(data => (data || []).map(x => this.NormalizeExecution(x))));
  }

  GetExecutionContext(PM_V2_Assignment_Item_ID: number): Observable<PM_V2_Execution_Context_Interface> {
    const params = new HttpParams()
      .set('PM_V2_Assignment_Item_ID', String(PM_V2_Assignment_Item_ID));

    return this.http.get<any>(
      this.BaseUrl + 'Daro/PM_V2_Execution/GetAssignmentItemContext', { params })
      .pipe(map(data => this.NormalizeExecutionContext(data)));
  }

  GetExecutionHistory(PM_V2_Assignment_Item_ID: number): Observable<PM_V2_Execution_History_Interface> {
    const params = new HttpParams()
      .set('PM_V2_Assignment_Item_ID', String(PM_V2_Assignment_Item_ID));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Execution/GetHistory', { params })
      .pipe(map(data => ({
        context: this.NormalizeExecutionContext(data ? (data.context || data.Context) : null),
        items: (((data && (data.items || data.Items)) || []) as any[]).map(x => this.NormalizeExecution(x))
      })));
  }

  GetExecutionSingle(id: number): Observable<PM_V2_Execution_Interface> {
    const params = new HttpParams()
      .set('id', String(id));

    return this.http
      .get<any>(this.BaseUrl + 'Daro/PM_V2_Execution/GetSingle', { params })
      .pipe(map(data => this.NormalizeExecution(data)));
  }

  AddExecution(model: PM_V2_Execution_Save_Interface): Observable<PM_V2_Execution_Interface> {
    const payload: any = {
      ...model,
      model: { ...model.model }
    };
    delete payload.model.Performed_By;
    delete payload.model.performed_By;
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Execution/Add', payload)
      .pipe(map(data => this.NormalizeExecution(data)));
  }

  ApproveExecution(model: PM_V2_Execution_Approve_Interface): Observable<PM_V2_Execution_Interface> {
    return this.http
      .post<any>(this.BaseUrl + 'Daro/PM_V2_Execution/Approve', model)
      .pipe(map(data => this.NormalizeExecution(data)));
  }

  // -------------------------
  // Existing DeviceBehfa search
  // -------------------------

  GetDeviceBehfaActive(CodeSearch: string): Observable<PM_V2_Device_Interface[]> {
    const params = new HttpParams()
      .set('CodeSearch', CodeSearch || '')
      .set('DateSearch', '')
      .set('GroupFirst', '0')
      .set('GroupSecond', '0')
      .set('withFilterGroup', 'false');

    return this.http
      .get<any>(this.BaseUrl + 'Daro/DeviceBehfa/GetSearchBehfaDeviceActive', { params })
      .pipe(map(data => {
        // شکل دقیق Response این endpoint در فایل‌های ایندکس‌شده مشخص نبود.
        // این بخش فقط Array یا wrapperهای رایج پروژه را نرمال می‌کند.
        const list = Array.isArray(data)
          ? data
          : ((data && (data.reuslt || data.result || data.data)) || []);

        return (list || []).map((x: any) => ({
          id: this.Pick(x, 'id', 'ID') || 0,
          codeBEHFA: this.Pick(x, 'codeBEHFA', 'CodeBEHFA') || '',
          description: this.Pick(x, 'description', 'Description') || '',
          manufacturer: this.Pick(x, 'manufacturer', 'Manufacturer') || '',
          model: this.Pick(x, 'model', 'Model') || ''
        }));
      }));
  }

  // -------------------------
  // Response normalization
  // -------------------------

  private NormalizeTemplate(x: any): PM_V2_Template_Interface {
    x = x || {};
    return {
      id: this.Pick(x, 'id', 'ID') || 0,
      Previous_PM_V2_Template_ID: this.Pick(x, 'Previous_PM_V2_Template_ID', 'previous_PM_V2_Template_ID'),
      PM_Code: this.Pick(x, 'PM_Code', 'pM_Code') || '',
      PM_Title: this.Pick(x, 'PM_Title', 'pM_Title') || '',
      Description: this.Pick(x, 'Description', 'description') || '',
      Version: this.Pick(x, 'Version', 'version'),
      Status: this.Pick(x, 'Status', 'status'),
      Time_Estimated: this.Pick(x, 'Time_Estimated', 'time_Estimated'),
      Default_Frequency: this.Pick(x, 'Default_Frequency', 'default_Frequency'),
      Default_Warning_Days: this.Pick(x, 'Default_Warning_Days', 'default_Warning_Days'),
      Date_Created: this.Pick(x, 'Date_Created', 'date_Created'),
      Created_By: this.Pick(x, 'Created_By', 'created_By'),
      Count_Item: this.Pick(x, 'Count_Item', 'count_Item')
    };
  }

  private NormalizeTemplateItem(x: any): PM_V2_Template_Item_Interface {
    x = x || {};
    return {
      id: this.Pick(x, 'id', 'ID') || 0,
      PM_V2_Template_ID: this.Pick(x, 'PM_V2_Template_ID', 'pM_V2_Template_ID'),
      Order_Item: this.Pick(x, 'Order_Item', 'order_Item'),
      Title_Item: this.Pick(x, 'Title_Item', 'title_Item') || '',
      Type_Item: this.Pick(x, 'Type_Item', 'type_Item') || '',
      Required_Is: this.Pick(x, 'Required_Is', 'required_Is'),
      Criteria_Acceptance: this.Pick(x, 'Criteria_Acceptance', 'criteria_Acceptance') || '',
      Instruction: this.Pick(x, 'Instruction', 'instruction') || '',
      Value_Frequency: this.Pick(x, 'Value_Frequency', 'value_Frequency'),
      Days_Warning: this.Pick(x, 'Days_Warning', 'days_Warning'),
      Active: this.Pick(x, 'Active', 'active')
    };
  }

  private NormalizeAssignment(x: any): PM_V2_Assignment_Interface {
    x = x || {};
    return {
      id: this.Pick(x, 'id', 'ID') || 0,
      PM_V2_Template_ID: this.Pick(x, 'PM_V2_Template_ID', 'pM_V2_Template_ID'),
      DeviceBehfa_ID: this.Pick(x, 'DeviceBehfa_ID', 'deviceBehfa_ID'),
      Active: this.Pick(x, 'Active', 'active'),

      PM_Code: this.Pick(x, 'PM_Code', 'pM_Code'),
      PM_Title: this.Pick(x, 'PM_Title', 'pM_Title'),
      Version: this.Pick(x, 'Version', 'version'),
      Template_Status: this.Pick(x, 'Template_Status', 'template_Status'),

      codeBEHFA: this.Pick(x, 'codeBEHFA', 'CodeBEHFA'),
      description: this.Pick(x, 'description', 'Description'),
      manufacturer: this.Pick(x, 'manufacturer', 'Manufacturer'),
      model: this.Pick(x, 'model', 'Model'),
      group_id: this.Pick(x, 'group_id', 'Group_ID'),
      GroupTitle: this.Pick(x, 'GroupTitle', 'groupTitle')
    };
  }

  private NormalizeAssignmentItem(x: any): any {
    return this.NormalizeMixed(x);
  }

  private NormalizeExecution(x: any): PM_V2_Execution_Interface {
    x = x || {};
    const y: any = this.NormalizeMixed(x);

    return {
      id: y.id || 0,
      PM_V2_Assignment_Item_ID: y.PM_V2_Assignment_Item_ID,
      Date_Due: y.Date_Due,
      Date_Execution: y.Date_Execution,
      Result: y.Result || '',
      Numeric_Value: y.Numeric_Value,
      Text_Value: y.Text_Value || '',
      Comment: y.Comment || '',
      Performed_By: y.Performed_By,
      Approved_By: y.Approved_By,
      Date_Execution_Persian: this.Pick(x, 'Date_Execution_Persian', 'date_Execution_Persian'),

      Assignment_ID: y.Assignment_ID,
      DeviceBehfa_ID: y.DeviceBehfa_ID,
      codeBEHFA: y.codeBEHFA,
      description: y.description,
      manufacturer: y.manufacturer,
      model: y.model,
      PM_Code: y.PM_Code,
      PM_Title: y.PM_Title,
      Version: y.Version,
      Template_Item_ID: y.Template_Item_ID,
      Title_Item: y.Title_Item,
      Type_Item: y.Type_Item,
      Required_Is: y.Required_Is
    };
  }

  private NormalizeExecutionContext(x: any): PM_V2_Execution_Context_Interface {
    x = x || {};
    const templateName = this.Pick(x, 'Template_Name', 'template_Name') || '';
    const equipmentCode = this.Pick(x, 'Equipment_Code', 'equipment_Code') || '';
    return {
      Equipment_ID: this.Pick(x, 'Equipment_ID', 'equipment_ID') || 0,
      Equipment_Code: equipmentCode,
      Equipment_Name: this.Pick(x, 'Equipment_Name', 'equipment_Name') || '',
      Template_ID: this.Pick(x, 'Template_ID', 'template_ID') || 0,
      Template_Code: this.Pick(x, 'Template_Code', 'template_Code') || '',
      Template_Name: templateName,
      Description: this.Pick(x, 'Description', 'description') || '',
      AcceptanceCriteria: this.Pick(x, 'AcceptanceCriteria', 'acceptanceCriteria') || '',
      Instruction: this.Pick(x, 'Instruction', 'instruction') || '',
      Activity_Name: this.Pick(x, 'Activity_Name', 'activity_Name') || '',
      Frequency: this.Pick(x, 'Frequency', 'frequency'),
      Assignment_Item_ID: this.Pick(x, 'Assignment_Item_ID', 'assignment_Item_ID') || 0,
      Type_Item: this.Pick(x, 'Type_Item', 'type_Item') || '',
      Date_Due: this.Pick(x, 'Date_Due', 'date_Due') || null,
      PM_Title: templateName,
      codeBEHFA: equipmentCode
    };
  }

  private NormalizeMixed(x: any): any {
    x = x || {};
    const y: any = {};

    Object.keys(x).forEach(key => y[key] = x[key]);

    const keys = [
      'PM_V2_Assignment_ID',
      'PM_V2_Assignment_Item_ID',
      'PM_V2_Template_Item_ID',
      'Assignment_ID',
      'Assignment_Item_ID',
      'DeviceBehfa_ID',
      'Template_Item_ID',
      'PM_Code',
      'PM_Title',
      'Template_Status',
      'Assignment_Active',
      'Assignment_Item_Active',
      'Template_Item_Active',
      'Date_Start',
      'Last_PM_Date',
      'Next_PM_Date',
      'Date_Due',
      'Date_Execution',
      'Result',
      'Numeric_Value',
      'Text_Value',
      'Comment',
      'Performed_By',
      'Approved_By',
      'Order_Item',
      'Title_Item',
      'Type_Item',
      'Required_Is',
      'Criteria_Acceptance',
      'Instruction',
      'Value_Frequency',
      'Days_Warning',
      'PM_Status',
      'Version'
      ,'Group', 'SubGroup', 'PM_V2_Template_ID', 'Template_Description', 'Time_Estimated',
      'Date_Start_Persian', 'Last_PM_Date_Persian', 'Next_PM_Date_Persian', 'Due_Status', 'Is_Never_Executed',
      'Days_Overdue', 'Execution_Count', 'Last_Execution_Date', 'Last_Execution_Date_Persian',
      'Remaining_Days',
      'Last_Result', 'Last_Numeric_Value', 'Last_Text_Value', 'Last_Comment',
      'Last_Performed_By', 'Last_Approved_By'
      ,'Equipment_ID', 'Equipment_Name', 'Equipment_Code', 'Equipment_Model',
      'Template_ID', 'Template_Code', 'Template_Name', 'Due_Item_Count',
      'Activity_Name', 'Frequency', 'Can_Execute'
      ,'Execution_ID', 'Date_Due_Persian', 'Date_Execution_Persian',
      'Description', 'Performer_Name'
    ];

    keys.forEach(k => {
      const camel = k.charAt(0).toLowerCase() + k.substring(1);
      const v = this.Pick(x, k, camel);
      if (v !== undefined) {
        y[k] = v;
      }
    });

    y.id = this.Pick(x, 'id', 'ID') || y.id || 0;
    y.Active = this.Pick(x, 'Active', 'active');
    y.codeBEHFA = this.Pick(x, 'codeBEHFA', 'CodeBEHFA');
    y.description = this.Pick(x, 'description', 'Description');
    y.manufacturer = this.Pick(x, 'manufacturer', 'Manufacturer');
    y.model = this.Pick(x, 'model', 'Model');

    return y;
  }

  private Pick(x: any, ...keys: string[]): any {
    for (const key of keys) {
      if (x && Object.prototype.hasOwnProperty.call(x, key)) {
        return x[key];
      }
    }
    return undefined;
  }
}
